// Cobranzas.utils.ts

import { saveAs } from "file-saver";
import type {
  CobranzasCampaignType,
  CobranzasGenesysEstado,
  CobranzasGenesysEstadoResponse,
  CobranzasGenesysIniciarResponse,
  CobranzasGenesysPayloadItem,
} from "../types";

const phoneKeys = ["Telefono_1", "Telefono_2", "Telefono_3"] as const;

export interface GenesysSendInfo {
  id?: string;
  estado?: CobranzasGenesysEstado;
  fase?: string;
  mensaje?: string;
  total?: number;
  yaEnviados?: number;
  pendientes?: number;
  enviados?: number;
  procesados?: number;
  faltantes?: number;
  contactosError?: number;
  bloquesOk?: number;
  bloquesError?: number;
  bloqueActual?: number;
  bloquesTotal?: number;
  fechaInicio?: string;
  errores: unknown[];
  message?: string;
}

const toOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const nestedData = (
  response: Record<string, unknown> | undefined
): Record<string, unknown> => {
  if (!response) return {};
  return response.data && typeof response.data === "object"
    ? (response.data as Record<string, unknown>)
    : {};
};

export const extractGenesysIniciarInfo = (
  response?: CobranzasGenesysIniciarResponse,
): Pick<GenesysSendInfo, "id" | "estado" | "message"> => {
  const root = (response ?? {}) as Record<string, unknown>;
  const data = nestedData(root);

  return {
    id: toOptionalString(root.id) ?? toOptionalString(data.id),
    estado: toOptionalString(root.estado) as CobranzasGenesysEstado | undefined,
    message:
      toOptionalString(root.message) ?? toOptionalString(data.message),
  };
};

export const extractGenesysEstadoInfo = (
  response?: CobranzasGenesysEstadoResponse,
): GenesysSendInfo => {
  if (!response) return { errores: [] };

  return {
    estado: toOptionalString(response.estado) as CobranzasGenesysEstado | undefined,
    fase: toOptionalString(response.fase),
    mensaje: toOptionalString(response.mensaje),
    total: toOptionalNumber(response.total),
    yaEnviados: toOptionalNumber(response.ya_enviados),
    pendientes: toOptionalNumber(response.pendientes),
    enviados: toOptionalNumber(response.enviados),
    procesados: toOptionalNumber(response.procesados),
    faltantes: toOptionalNumber(response.faltantes),
    bloquesOk: toOptionalNumber(response.bloques_ok),
    bloquesError: toOptionalNumber(response.bloques_error),
    bloqueActual: toOptionalNumber(response.bloque_actual),
    bloquesTotal: toOptionalNumber(response.bloques_total),
    fechaInicio: toOptionalString(response.fecha_inicio),
    contactosError: toOptionalNumber(response.contactos_error),
    errores: Array.isArray(response.errores) ? response.errores : [],
  };
};

export const extractGenesysErrores = (response: unknown): unknown[] => {
  if (Array.isArray(response)) return response;

  if (response && typeof response === "object") {
    const data = (response as Record<string, unknown>).data;
    if (Array.isArray(data)) return data;
  }

  return [];
};

export interface CobranzasRow {
  _id: string;
  idContrato: string;
  identificacion: string;
  nombreCliente: string;
  sector: string;
  valor: string;
  valorPlan: string;
  deuda: string;
  mes: string;
  correo: string;
  telefonos: string;
}

const formatMoney = (value?: number | null): string =>
  typeof value === "number" ? `$${value.toFixed(2)}` : "";

const cleanPhone = (phone?: string): string | null => {
  if (!phone) return null;
  const trimmed = phone.trim();
  return trimmed === "" || trimmed === "null" ? null : trimmed;
};

export const buildCobranzasRow = (
  item: CobranzasGenesysPayloadItem,
  index: number,
): CobranzasRow => {
  const contact = item.data ?? {};
  const phoneStatus = item.phoneNumberStatus ?? {};

  const telefonos = phoneKeys
    .map((key) => {
      const numero = cleanPhone(contact[key]);
      if (!numero) return null;
      // phoneNumberStatus indica si Genesys puede llamar al número
      return phoneStatus[key]?.callable === false
        ? `${numero} (no llamable)`
        : numero;
    })
    .filter((phone): phone is string => phone !== null)
    .join(", ");

  const nombreCliente = [contact.Nombre, contact.Apellido]
    .filter((part) => !!part && part.trim() !== "")
    .join(" ");

  return {
    _id: `${contact.idContrato || "sin-contrato"}-${index}`,
    idContrato: contact.idContrato ?? "",
    identificacion: contact.identificacion ?? "",
    nombreCliente,
    sector: contact.Sector ?? "",
    valor: formatMoney(contact.Valor),
    valorPlan: formatMoney(contact.valor_plan),
    deuda: formatMoney(contact.deuda),
    mes: contact.Mes ?? "",
    correo: contact.correo ?? "",
    telefonos,
  };
};

const excelFileName = (campania: CobranzasCampaignType): string => {
  const fecha = new Date().toISOString().slice(0, 10);
  return `cobranzas_${campania.toLowerCase()}_${fecha}.xlsx`;
};

export const exportCobranzasPayloadToExcel = (
  items: CobranzasGenesysPayloadItem[],
  campania: CobranzasCampaignType
): void => {
  const rows = items
    .map((item) => item.data ?? {})
    .map((contact) => {
      const row: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(contact)) {
        row[key] = value ?? "";
      }
      return row;
    });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  if (rows.length > 0) {
    worksheet["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: key === "idLinkPagos" ? 40 : Math.max(10, key.length + 4),
    }));
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Contactos Genesys");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });

  const archivo = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(archivo, excelFileName(campania));
};
